import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useGlobalStore } from "@/store/global";
import { usePowerStore } from "@/store/power";
import appUpdate from "@/service/appUpdate";
import { isEnterpriseLevel } from "@/constants/userLevel";

// 静态路由文件
import other from "./other";

const routes: Array<RouteRecordRaw> = [
    {
        path: "/",
        component: () => import("@/views/RootEntry.vue"),
        children: [
            {
                path: "",
                component: () => import("@/views/main/Main.vue"),
                children: [
                    {
                        path: "",
                        component: { template: "<div />" },
                    },
                    {
                        path: "home",
                        redirect: "/creation",
                    },
                    {
                        path: "resource",
                        meta: { title: "资产库" },
                        component: () => import("@/views/resource/index.vue"),
                    },
                    {
                        path: "creation",
                        meta: { title: "万象智作" },
                        component: () => import("@/views/creation/index.vue"),
                    },
                    {
                        /** 全部项目：万象智作二级页，列表从首页「查看全部任务」进入 */
                        path: "creation/tasks",
                        meta: { title: "全部项目" },
                        component: () => import("@/views/creation/AllTasksPage.vue"),
                    },
                    {
                        /** 切片裂变向导：挂在创作下；列表在 /creation */
                        path: "creation/slice-fission",
                        meta: { title: "切片裂变" },
                        component: () => import("@/views/sliceFission/index.vue"),
                    },
                    {
                        /** 兼容旧链接：有 batchId 进向导，否则回创作页切片 Tab */
                        path: "slice-fission",
                        redirect: (to) => {
                            if (to.query.batchId) {
                                return {
                                    path: "/creation/slice-fission",
                                    query: to.query,
                                };
                            }
                            return {
                                path: "/creation",
                                query: { tab: "slice-fission" },
                            };
                        },
                    },
                    {
                        path: "inspiration",
                        meta: { title: "灵感中心" },
                        component: () => import("@/views/inspiration/index.vue"),
                    },
                    {
                        path: "my-works",
                        meta: { title: "我的作品" },
                        component: () => import("@/views/myWorks/index.vue"),
                    },
                    {
                        path: "tools",
                        meta: { title: "百宝箱" },
                        component: () => import("@/views/tools/index.vue"),
                    },
                    {
                        path: "tools/one-click-hd",
                        meta: { title: "一键高清" },
                        component: () => import("@/views/tools/oneClickHd/index.vue"),
                    },
                    {
                        path: "tools/remove-subtitle",
                        meta: { title: "字幕擦除" },
                        component: () => import("@/views/tools/removeSubtitle/index.vue"),
                    },

                    {
                        path: "breakdown",
                        meta: { title: "爆款拆解" },
                        component: () => import("@/views/breakdown/index.vue"),
                    },
                    {
                        path: "assets",
                        redirect: "/resource",
                    },
                    {
                        path: "segmentVideo",
                        meta: { title: "视频切片" },
                        component: () => import("@/views/segmentVideo/index.vue"),
                    },
                    {
                        /** 元素工坊向导：挂在创作下；列表在 /creation（实现目录 elementWorkShop） */
                        path: "creation/element-workshop",
                        meta: { title: "元素工坊" },
                        component: () => import("@/views/elementWorkShop/index.vue"),
                    },
                    {
                        /** 逆向工坊向导：挂在创作下；列表在 /creation */
                        path: "creation/reverse-workshop",
                        meta: { title: "逆向工坊" },
                        component: () => import("@/views/create/ReverseWorkshopPage.vue"),
                    },
                    {
                        /**
                         * 兼容旧 /replication：有向导参数进对应工坊，否则回创作页。
                         * type/preferredType=1 → 元素；其余有参默认逆向（爆款库复刻）。
                         */
                        path: "replication",
                        redirect: (to) => {
                            const q = to.query;
                            const typeRaw = q.type ?? q.preferredType;
                            const typeStr = Array.isArray(typeRaw) ? typeRaw[0] : typeRaw;
                            const hasWizard =
                                Boolean(q.recordId) ||
                                Boolean(q.sourceId) ||
                                q.view === "wizard" ||
                                Boolean(typeStr);
                            if (!hasWizard) {
                                return {
                                    path: "/creation",
                                    query: { tab: "reverse-workshop" },
                                };
                            }
                            const path =
                                String(typeStr) === "1"
                                    ? "/creation/element-workshop"
                                    : "/creation/reverse-workshop";
                            return { path, query: q };
                        },
                    },
                    {
                        /** 批量复刻向导：挂在创作下；列表在 /creation */
                        path: "creation/batch-replication",
                        meta: { title: "批量复刻" },
                        component: () => import("@/views/batchReplication/index.vue"),
                    },
                    {
                        path: "workshop",
                        meta: { title: "一键梦工厂" },
                        component: () => import("@/views/workshop/index.vue"),
                    },
                    {
                        /** 兼容旧链接：有向导参数进向导，否则回创作页批量复刻 Tab */
                        path: "batch-replication",
                        redirect: (to) => {
                            const q = to.query;
                            if (q.recordId || q.view === "wizard" || q.sourceId || q.type) {
                                return {
                                    path: "/creation/batch-replication",
                                    query: q,
                                };
                            }
                            return {
                                path: "/creation",
                                query: { tab: "batch-replication" },
                            };
                        },
                    },
                    {
                        path: "digital-human",
                        redirect: "/resource",
                    },
                    {
                        /** 自主创作向导：挂在创作下；列表在 /creation */
                        path: "creation/ai-creation",
                        meta: { title: "自主创作" },
                        component: () => import("@/views/aiCreation/index.vue"),
                    },
                    {
                        /** 兼容旧链接：有 wizard session 进向导，否则回创作页自主创作 Tab */
                        path: "ai-creation",
                        redirect: (to) => {
                            if (to.query.view === "wizard") {
                                return {
                                    path: "/creation/ai-creation",
                                    query: to.query,
                                };
                            }
                            return {
                                path: "/creation",
                                query: { tab: "ai-creation" },
                            };
                        },
                    },
                    {
                        /** 投放罗盘：投放统计 + 素材分析（query.view=analyze） */
                        path: "qianchuan",
                        meta: { title: "投放罗盘" },
                        component: () => import("@/views/qanchuan/index.vue"),
                    },
                    {
                        /** 兼容旧路径：/qanchuan → /qianchuan */
                        path: "qanchuan",
                        redirect: "/qianchuan",
                    },
                    {
                        /** 旧「巨量登录」入口并入千川 · 素材分析 */
                        path: "ocean-login",
                        redirect: { path: "/qianchuan", query: { view: "analyze" } },
                    },
                    {
                        /** 切片裂变：步骤1 素材上传与拓影解析 */
                        path: "slice-fission",
                        meta: { title: "切片裂变" },
                        component: () => import("@/views/sliceFission/index.vue"),
                    },
                    {
                        path: "pricing",
                        meta: { title: "定价充值" },
                        component: () => import("@/views/pricing/index.vue"),
                    },
                    {
                        path: "profile",
                        meta: { title: "个人中心" },
                        component: () => import("@/views/profile/index.vue"),
                    },
                    {
                        path: "tasks",
                        meta: { title: "任务管理" },
                        component: () => import("@/views/taskManagement/index.vue"),
                    },
                    {
                        path: "pay-test",
                        meta: { title: "支付测试" },
                        component: () => import("@/views/payTest/index.vue"),
                    },
                    {
                        path: "unlimited-canvas",
                        meta: { title: "万象画布" },
                        component: () => import("@/views/unlimitedCanvas/ProjectListPage.vue"),
                    },
                    {
                        path: "unlimited-canvas/all",
                        meta: { title: "全部画布" },
                        component: () => import("@/views/unlimitedCanvas/AllCanvasesPage.vue"),
                    },
                    {
                        path: "unlimited-canvas/:projectId",
                        meta: { title: "万象画布" },
                        component: () => import("@/views/unlimitedCanvas/index.vue"),
                    },
                    {
                        path: "feedback",
                        meta: { title: "用户反馈" },
                        component: () => import("@/views/feedback/index.vue"),
                    },
                    {
                        path: "user-management",
                        meta: { title: "用户管理" },
                        component: () => import("@/views/userManagement/index.vue"),
                    },
                    {
                        path: "admin/logs",
                        meta: { title: "系统管理", requiresAdmin: true },
                        component: () => import("@/views/logAdmin/index.vue"),
                    },
                    {
                        path: "admin/tenants",
                        meta: { title: "企业管理", requiresAdmin: true },
                        component: () => import("@/views/adminTenant/index.vue"),
                    },
                    {
                        path: "admin/power",
                        meta: { title: "影豆管理", requiresAdmin: true },
                        component: () => import("@/views/adminPower/index.vue"),
                    },
                    {
                        path: "admin/finance",
                        meta: { title: "财务统计", requiresAdmin: true },
                        component: () => import("@/views/adminFinance/index.vue"),
                    },
                    {
                        path: "tenant-member",
                        meta: { title: "团队管理", requiresTenantAdmin: true },
                        redirect: { path: "/profile", query: { section: "team" } },
                    },
                    {
                        path: "tenant-member/list",
                        meta: { title: "团队管理", requiresTenantAdmin: true },
                        redirect: { path: "/profile", query: { section: "team" } },
                    },
                    {
                        path: "tenant-member/records",
                        meta: { title: "团队管理", requiresTenantAdmin: true },
                        redirect: { path: "/profile", query: { section: "team" } },
                    },
                    ...other,
                ],
            },
        ],
    },
    {
        path: "/login",
        component: () => import("@/views/login/Login.vue"),
    },
    {
        /** 投放回流原型：独立页，示例数据，不走主布局请求 */
        path: "/my-works/delivery-prototype",
        meta: { title: "作品投放回流原型" },
        component: () => import("@/views/myWorks/deliveryPrototype/WorkDeliveryPrototype.vue"),
    },
    {
        /** 创作教学视频：与 login 同级独立全屏页，新标签打开 */
        path: "/teaching-videos",
        meta: { title: "教学视频" },
        component: () => import("@/views/creation/TeachingVideosPage.vue"),
    },
    {
        /** 巨量远程登录画面：与 Main 同级独立全屏页，由巨量登录页 window.open 打开 */
        path: "/ocean-login/remote",
        meta: { title: "巨量官方登录" },
        component: () => import("@/views/oceanLogin/remote.vue"),
    },
    {
        name: "404",
        path: "/:catchAll(.*)",
        component: () => import("@/views/other/404.vue"),
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

router.beforeEach(async (to, from, next) => {
    // 0.0> 路由切换时检测新版本，hash 不一致则刷新页面 -----------------------------------------------
    if (import.meta.env.PROD && from.name !== undefined) {
        const reloaded = await appUpdate.checkOnRouteChange(to.fullPath);
        if (reloaded) return;
    }

    // 0.1 页面顶部进度条：开始显示进度 ------------------------------------------------------------------
    nprogressStart();

    // 1.0> 动态读取 config.json 配置 -------------------------------------------------------------------
    const globalStore = useGlobalStore();
    if (globalStore.config.length <= 0) {
        const res = await fetch(utils.common.getFullPath(router, "/config.json"));
        globalStore.config = await res.json();
    }

    // 2.0> 设置页面meta -------------------------------------------------------------------------------
    if (to.meta.title) {
        window.document.title = (common.getEnvVar("VITE_APP_NAME") + " - " + to.meta.title) as string;
    } else {
        window.document.title = common.getEnvVar("VITE_APP_NAME");
    }

    // 3.0> 登录校验 ---------------------------------------------------
    const token = common.getToken();
    const signKey = common.getSignKey();
    const isGuest = token === null || signKey === null;

    // 未登录用户：只能访问 /（Landing）、/login，以及不请求接口的投放回流原型
    if (
        isGuest &&
        to.path !== "/" &&
        to.path !== "/login" &&
        to.path !== "/my-works/delivery-prototype" &&
        to.name !== "404"
    ) {
        next({ path: "/login", query: { redirect: to.fullPath } });
        return;
    }

    // 权限路由：未登录先去登录
    if (isGuest && (to.meta.requiresAdmin || to.meta.requiresTenantAdmin)) {
        next("/login");
        return;
    }

    // 记录用户访问的最后一个业务页面（/、/login、教学视频、巨量远程登录独立页不记录），用于 Landing "进入系统"的跳转
    if (
        to.path !== "/" &&
        to.path !== "/login" &&
        to.path !== "/teaching-videos" &&
        to.path !== "/ocean-login/remote"
    ) {
        localStorage.setItem("last_page", to.fullPath);
    }

    if (isGuest) {
        next();
        return;
    }

    // 已登录用户访问登录页时直接放行，避免失效凭证阻塞重新登录
    if (to.path === "/login") {
        next();
        return;
    }

    // 4.0> 动态获取用户信息（已登录用户） ----------------------------------------------------------------
    try {
        await common.getUserInfo(token, signKey as string);
        // 4.1> 幂等初始化影豆类型枚举 + 扣费规则（刷新后首次进入即可用）
        await usePowerStore().init();
    } catch {
        common.signOut();
        sessionStorage.setItem("session_expired", "1");
        next({ path: "/login", query: { redirect: to.fullPath } });
        return;
    }

    // 5.0> 动态获取路由信息 -----------------------------------------------------------------------------
    // const routersData = await common.getRouters(token);

    // 6.0> 直接放行"404" ---------------------------------------------------------------------
    if (to.name == "404") {
        next();
        return;
    }

    // 7.0> 管理员路由校验 ---------------------------------------------------------------------------
    if (to.meta.requiresAdmin) {
        const userInfo = common.getSynUserInfo();
        if (!userInfo || userInfo.role !== "admin") {
            next("/home");
            return;
        }
    }

    if (to.meta.requiresTenantAdmin) {
        // 成员管理：需企业管理员 role=1 且 ENTERPRISE 登录身份
        const userInfo = common.getSynUserInfo();
        if (
            !userInfo ||
            userInfo.role !== "1" ||
            userInfo.loginMode !== "ENTERPRISE" ||
            !isEnterpriseLevel(userInfo.level)
        ) {
            next("/home");
            return;
        }
    }

    next();
});

router.afterEach(() => {
    // 0.0 页面顶部进度条：关闭 ---------------------------------------------------------------------
    nprogressClose();
});

router.onError((err) => {
    if (appUpdate.isChunkLoadError(err)) {
        appUpdate.reloadForUpdate(router.currentRoute.value.fullPath);
    }
});

export default router;
